// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// D5 tier-gating policy -- the single source of truth for which access tier each
// gated surface requires, shared by the server (enforcement) and the app (UX
// gating: hide/disable controls + upgrade affordances). Lives in @simlin/core so
// both consume the SAME thresholds; the server re-exports it (src/server/
// tier-policy.ts) for backwards-compatible imports.
//
// Freemium model (Williams, refined 2026-06-24). The access ladder is
// public < student < member < advanced:
//   - public:   build a model in-session only -- cannot save. Upgrade prompt
//               visible. (Anonymous sessions resolve to `public` via DEFAULT_TIER.)
//   - student:  the baseline registered tier -- save, share/publish, download,
//               upload, simulation, and sensitivity. New sign-ups default to
//               `student` (see schema.prisma), so "sign up to save" is the model.
//   - member:   scenario planning (extends sim/sensitivity) + B* (coherence).
//   - advanced: team-based planning, including gamification and microworlds, and
//               the hosted MCP/agent surface.
//
// Thresholds split into two groups:
//   ENFORCED below have a server gate-point today (create/save + publish + the
//   D6 content write API).
//   DEFINED-PENDING have no gate-point yet because the capability is client-side
//   (download/upload, simulation) or unbuilt (B*, teams, MCP). They are recorded
//   here so the gate is a single edit the moment the feature gains a server-side
//   enforcement point -- the same pattern as MCP (#42).

import { Tier, tierAtLeast } from './tier';

// --- Enforced (server gate-point exists) -------------------------------------

// Create/save your own models. Gated server-side in api.ts (tierGate).
export const OWNERSHIP_MIN_TIER: Tier = Tier.Student;

// Publish a public (shared/embeddable) model. Gated server-side in api.ts.
export const SHARE_MIN_TIER: Tier = Tier.Student;

// Create/edit content-library entries (templates, archetypes, sectors). Gated
// server-side on the D6 content write API (POST/PUT /api/v1/...). Reading the
// library is open; authoring is a member surface.
export const CONTENT_WRITE_MIN_TIER: Tier = Tier.Member;

// --- Defined, not yet enforced -----------------------------------------------
// Capabilities that are client-side or unbuilt today; thresholds recorded so
// enforcement is a single edit once a server-side gate-point exists.

// Download/export and upload/import. Client-side file ops today (no server
// gate-point); these back soft, UX-level gates in the SPA.
export const DOWNLOAD_MIN_TIER: Tier = Tier.Student;
export const UPLOAD_MIN_TIER: Tier = Tier.Student;

// Simulation + sensitivity are Student capabilities (Williams 2026-06-24). Basic
// running stays open in the public "play" experience because it runs client-side
// in WASM; these thresholds gate the simulation/sensitivity *capability* surface
// once it has a server/UI enforcement point.
export const SIMULATION_MIN_TIER: Tier = Tier.Student;
export const SENSITIVITY_MIN_TIER: Tier = Tier.Student;

// Scenario planning (extends simulation/sensitivity) and B* (coherence) are
// Member surfaces.
export const SCENARIO_MIN_TIER: Tier = Tier.Member;
export const BSTAR_MIN_TIER: Tier = Tier.Member;

// Team-based planning, including gamification and microworlds. Advanced surface,
// unbuilt (Phase 3 / enterprise).
export const TEAMS_MIN_TIER: Tier = Tier.Advanced;

// MCP / agent access. Not enforced in simlin-serve's local MCP (no identity to
// gate on -- see D5.8 in docs/d5/d5-acceptance.md and tech-debt #42); defined
// here for a future hosted/authenticated MCP surface and the acceptance matrix.
export const MCP_MIN_TIER: Tier = Tier.Advanced;

// --- Predicates --------------------------------------------------------------

// Whether a user of the given tier may create/edit content-library entries.
export function canWriteContent(tier: Tier): boolean {
  return tierAtLeast(tier, CONTENT_WRITE_MIN_TIER);
}

// Whether a user of the given tier may create/save their own models.
export function canCreate(tier: Tier): boolean {
  return tierAtLeast(tier, OWNERSHIP_MIN_TIER);
}

// Whether a user of the given tier may publish a public/shared model.
export function canPublish(tier: Tier): boolean {
  return tierAtLeast(tier, SHARE_MIN_TIER);
}

// Whether a user of the given tier may download/export a model. Soft (UX) gate:
// download is a client-side op, so this is advisory, not a security boundary.
export function canDownload(tier: Tier): boolean {
  return tierAtLeast(tier, DOWNLOAD_MIN_TIER);
}

// Whether a user of the given tier may upload/import a model. Soft (UX) gate,
// as with canDownload.
export function canUpload(tier: Tier): boolean {
  return tierAtLeast(tier, UPLOAD_MIN_TIER);
}
